// src/services/api.js - Enhanced version with Support Tickets
import axios from 'axios';
import { auth } from '../firebase/config';
import { API_BASE_URL } from '../config';
import { reportApiError } from '../utils/errorReporter';
import { normalizeApiError } from '../utils/apiError';
import { getAttribution } from '../utils/attribution';
import { safeSessionStorage } from '../utils/safeStorage';

// Map human-readable file types stored in DB to real MIME types
const FILE_TYPE_MIME = {
  PDF: 'application/pdf',
  MP4: 'video/mp4', MOV: 'video/quicktime', WEBM: 'video/webm', AVI: 'video/x-msvideo',
  MP3: 'audio/mpeg', WAV: 'audio/wav', AAC: 'audio/aac', FLAC: 'audio/flac', M4A: 'audio/mp4',
  PNG: 'image/png', JPG: 'image/jpeg', JPEG: 'image/jpeg', WEBP: 'image/webp', GIF: 'image/gif',
};

function makeBlobUrl(data, serverContentType, fileTypeHint) {
  const hintMime = fileTypeHint ? FILE_TYPE_MIME[fileTypeHint.toUpperCase()] : null;
  const serverMime = serverContentType?.split(';')[0]?.trim();
  // Prefer hint > valid server type > fallback
  const mimeType = hintMime
    || (serverMime && serverMime !== 'application/octet-stream' ? serverMime : null)
    || 'application/octet-stream';
  return URL.createObjectURL(new Blob([data], { type: mimeType }));
}

// No default timeout means a stalled connection — common on the mobile/patchy
// networks a lot of our creators are on — hangs every awaited call forever:
// the UI is stuck on "Uploading..." with nothing to catch, log, or retry.
// 60s is generous for the JSON calls this instance mostly carries (a few
// seconds even for a heavy DynamoDB-backed admin query); the handful of
// calls that are legitimately slower — PayU reconciliation, large file
// previews — override it explicitly per-request below rather than raising
// the default and reopening the hang for everyone else.
const DEFAULT_TIMEOUT_MS = 60000;

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: DEFAULT_TIMEOUT_MS,
  headers: {
    'Content-Type': 'application/json'
  },
});

// Add Firebase token to all requests
api.interceptors.request.use(async (config) => {
  const user = auth.currentUser;
  if (user) {
    const token = await user.getIdToken();
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Error handling interceptor. Report creator-facing issues (best-effort) then
// re-reject so existing call-site handling is unchanged.
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Flatten FastAPI 422 validation `detail` arrays to a string so no render
    // site can crash on an object (React #31). Runs for every call on this
    // shared instance (creator + admin).
    try { normalizeApiError(error); } catch (_) { /* never block on this */ }
    // Banned mid-session: the backend gate answers 403 + code. Tell
    // ProtectedRoute (it swaps every creator page for AccountBanned) instead of
    // leaving each page to show its own "something went wrong".
    try {
      if (error?.response?.data?.code === 'account_banned') {
        window.dispatchEvent(new CustomEvent('mintlink:account-blocked', { detail: 'banned' }));
      }
    } catch (_) { /* never block on this */ }
    try { reportApiError(error); } catch (_) { /* never block on reporting */ }
    return Promise.reject(error);
  }
);


// API Methods
export const apiService = {
  // ============================================
  // CREATOR APIs
  // ============================================
  
  createCreator: async (data) => {
    const response = await api.post('/api/creators', data);
    return response.data;
  },

  getMyProfile: async () => {
    const response = await api.get('/api/creators/me');
    return response.data;
  },

  // Is this product URL free? Advisory only — the save re-checks, because two
  // creators can pass this at the same moment and only one can win.
  // `assetId` is the product being edited, so its own current URL and its own
  // retired ones don't come back as clashes with itself.
  checkAssetSlug: async (slug, assetId) => {
    const response = await api.get('/api/assets/slug-check', {
      params: { slug, ...(assetId ? { asset_id: assetId } : {}) },
    });
    return response.data;
  },

  acceptPhysicalTerms: async () => {
    const response = await api.post('/api/creators/me/accept-physical-terms');
    return response.data;
  },

  // Versioned Terms re-acceptance (the 5 Aug 2026 revision). Distinct from
  // acceptPhysicalTerms above: that is the one-time seller agreement, this
  // tracks WHICH revision of the Terms a creator has agreed to.
  getMyTermsStatus: async () => {
    const response = await api.get('/api/creators/me/terms-status');
    return response.data;
  },

  acceptTermsVersion: async () => {
    const response = await api.post('/api/creators/me/accept-terms-version');
    return response.data;
  },

  acceptReviewServiceTerms: async () => {
    const response = await api.post('/api/creators/me/accept-review-service-terms');
    return response.data;
  },

  // Services (the freelance marketplace) has its own agreement, separate from
  // the Review My Work one above: different fee split, different escrow
  // release trigger, and a GST declaration Review My Work doesn't carry.
  acceptGigSellerTerms: async (body = {}) => {
    const response = await api.post('/api/creators/me/accept-gig-terms', body);
    return response.data;
  },

  // MintLink Forms has its own agreement too — enforced server-side (unlike
  // the two above), since a form hands a buyer a document the creator
  // uploaded rather than the creator's own listing text.
  acceptPdfFormsTerms: async () => {
    const response = await api.post('/api/creators/me/accept-pdf-forms-terms');
    return response.data;
  },

  // ── MintLink Forms ──────────────────────────────────────────────────────
  createPdfForm: async (data) => {
    const response = await api.post('/api/pdf-forms', data);
    return response.data;
  },

  updatePdfForm: async (formId, data) => {
    const response = await api.patch(`/api/creators/me/pdf-forms/${formId}`, data);
    return response.data;
  },

  getMyPdfForms: async () => {
    const response = await api.get('/api/creators/me/pdf-forms');
    return response.data;
  },

  getMyPdfForm: async (formId) => {
    const response = await api.get(`/api/creators/me/pdf-forms/${formId}`);
    return response.data;
  },

  deletePdfForm: async (formId) => {
    const response = await api.delete(`/api/creators/me/pdf-forms/${formId}`);
    return response.data;
  },

  getPdfFormBySlug: async (slug) => {
    const response = await api.get(`/api/pdf-forms/slug/${slug}`);
    return response.data;
  },

  submitPdfForm: async (slug, data) => {
    const response = await api.post(`/api/pdf-forms/${slug}/submit`, data);
    return response.data;
  },

  getPdfFormDownloadUrl: async (token) => {
    const response = await api.get(`/api/pdf-forms/download/${token}`);
    return response.data;
  },

  // Spends one of 3 free test downloads BEFORE the client fills the PDF —
  // the fill itself stays entirely in the browser (pdf-lib, in
  // FormsEditor.jsx), so this call is the only record that it happened and
  // the only thing standing between "preview my fields" and an unlimited free
  // copy of the finished product. Throws (409/429) once the form is out of
  // attempts — callers must not fill/download unless this resolves first.
  registerPdfFormTestFill: async (formId) => {
    const response = await api.post(`/api/creators/me/pdf-forms/${formId}/test-fill`);
    return response.data;
  },

  // ── MintLink Quiz ───────────────────────────────────────────────────────
  // Teacher-side. getMyQuiz returns the questions WITH their answers; the
  // public calls below never do — see app/routes/quizzes.py.
  createQuiz: async (data) => {
    const response = await api.post('/api/quizzes', data);
    return response.data;
  },

  updateQuiz: async (quizId, data) => {
    const response = await api.patch(`/api/creators/me/quizzes/${quizId}`, data);
    return response.data;
  },

  getMyQuizzes: async () => {
    const response = await api.get('/api/creators/me/quizzes');
    return response.data;
  },

  getMyQuiz: async (quizId) => {
    const response = await api.get(`/api/creators/me/quizzes/${quizId}`);
    return response.data;
  },

  deleteQuiz: async (quizId) => {
    const response = await api.delete(`/api/creators/me/quizzes/${quizId}`);
    return response.data;
  },

  getQuizResults: async (quizId) => {
    const response = await api.get(`/api/creators/me/quizzes/${quizId}/results`);
    return response.data;
  },

  // Student-side — all public, no auth. The attempt token in the URL is the
  // whole credential, exactly like /ticket/:token and /member/:token.
  getQuizBySlug: async (slug) => {
    const response = await api.get(`/api/quizzes/slug/${slug}`);
    return response.data;
  },

  // → free: { status:'confirmed', attempt_token, quiz_url }
  // → paid: { status:'payment_required', payment_url, payment_params }
  buyQuiz: async (slug, data) => {
    const response = await api.post(`/api/quizzes/${slug}/buy`, data);
    return response.data;
  },

  getQuizAttempt: async (token) => {
    const response = await api.get(`/api/quiz-attempts/${token}`);
    return response.data;
  },

  // Idempotent: calling it again on a running attempt returns the deadline
  // that already exists rather than granting fresh time.
  startQuizAttempt: async (token) => {
    const response = await api.post(`/api/quiz-attempts/${token}/start`);
    return response.data;
  },

  // Autosave. Fire-and-forget on every answer change — it is what lets a
  // student whose connection drops still be graded on what they had chosen.
  saveQuizAnswers: async (token, answers) => {
    const response = await api.post(`/api/quiz-attempts/${token}/save`, { answers });
    return response.data;
  },

  submitQuizAttempt: async (token, answers, autoSubmitted = false) => {
    const response = await api.post(`/api/quiz-attempts/${token}/submit`, {
      answers,
      auto_submitted: autoSubmitted,
    });
    return response.data;
  },

  resendQuizResultPdf: async (token) => {
    const response = await api.post(`/api/quiz-attempts/${token}/resend-pdf`);
    return response.data;
  },

  // ── MintLink Quiz Bundles ────────────────────────────────────────────────
  // Pack several of a creator's own quizzes into one pass; a buyer who pays
  // once can retake anything in it, gated by expires_at only — see
  // app/routes/quiz_bundles.py.
  createQuizBundle: async (data) => {
    const response = await api.post('/api/quiz-bundles', data);
    return response.data;
  },

  updateQuizBundle: async (bundleId, data) => {
    const response = await api.patch(`/api/creators/me/quiz-bundles/${bundleId}`, data);
    return response.data;
  },

  getMyQuizBundles: async () => {
    const response = await api.get('/api/creators/me/quiz-bundles');
    return response.data;
  },

  // ── Courses & Content (collections + the buyer library) ──────────────────
  // See app/routes/collections.py. Library calls take the buyer token
  // explicitly: on a creator's site it lives in that origin's localStorage
  // (utils/libraryAuth.js), not the dashboard's sessionStorage.
  getMyCollections: async () => (await api.get('/api/creators/me/collections')).data,
  getMyCollection: async (id) => (await api.get(`/api/creators/me/collections/${id}`)).data,
  createCollection: async (data) => (await api.post('/api/creators/me/collections', data)).data,
  updateCollection: async (id, data) => (await api.put(`/api/creators/me/collections/${id}`, data)).data,
  deleteCollection: async (id) => (await api.delete(`/api/creators/me/collections/${id}`)).data,
  getCollectionBuyers: async (id) => (await api.get(`/api/creators/me/collections/${id}/buyers`)).data,

  getPublicCollections: async (username) =>
    (await api.get(`/api/collections/public/${encodeURIComponent(username)}`)).data,
  getPublicCollection: async (username, id) =>
    (await api.get(`/api/collections/public/${encodeURIComponent(username)}/${id}`)).data,
  previewCollectionDiscount: async (username, id, code) =>
    (await api.post(`/api/collections/public/${encodeURIComponent(username)}/${id}/discount`, { code })).data,
  buyCollection: async (username, id, payload, buyerToken) =>
    (await api.post(`/api/collections/public/${encodeURIComponent(username)}/${id}/buy`, payload, {
      headers: { 'X-Buyer-Token': buyerToken },
    })).data,

  getLibrary: async (username, buyerToken) =>
    (await api.get(`/api/library/${encodeURIComponent(username)}`, {
      headers: { 'X-Buyer-Token': buyerToken },
    })).data,
  getLibraryCollection: async (username, id, buyerToken) =>
    (await api.get(`/api/library/${encodeURIComponent(username)}/${id}`, {
      headers: { 'X-Buyer-Token': buyerToken },
    })).data,
  getLibraryItem: async (username, id, itemId, buyerToken) =>
    (await api.get(`/api/library/${encodeURIComponent(username)}/${id}/items/${itemId}`, {
      headers: { 'X-Buyer-Token': buyerToken },
    })).data,

  getMyQuizBundle: async (bundleId) => {
    const response = await api.get(`/api/creators/me/quiz-bundles/${bundleId}`);
    return response.data;
  },

  deleteQuizBundle: async (bundleId) => {
    const response = await api.delete(`/api/creators/me/quiz-bundles/${bundleId}`);
    return response.data;
  },

  // Buyer-side — all public, no auth.
  getQuizBundleBySlug: async (slug) => {
    const response = await api.get(`/api/quiz-bundles/slug/${slug}`);
    return response.data;
  },

  buyQuizBundle: async (slug, data) => {
    const response = await api.post(`/api/quiz-bundles/${slug}/buy`, data);
    return response.data;
  },

  getBundlePass: async (token) => {
    const response = await api.get(`/api/bundle-passes/${token}`);
    return response.data;
  },

  // → { attempt_token, quiz_url } — opens in the exact same /quiz/:token
  // page a standalone purchase does. Calling this again on the same quiz
  // is how a retake happens; each call mints an independent attempt.
  startBundleQuiz: async (token, quizId) => {
    const response = await api.post(`/api/bundle-passes/${token}/quizzes/${quizId}/start`);
    return response.data;
  },

  // ── Admin ────────────────────────────────────────────────────────────────
  getAdminPdfFormsOverview: async () => {
    const response = await api.get('/api/admin/pdf-forms/overview');
    return response.data;
  },

  getAdminPdfForms: async (params = {}) => {
    const response = await api.get('/api/admin/pdf-forms', { params });
    return response.data;
  },

  getAdminPdfFormDetail: async (creatorId, formId) => {
    const response = await api.get(`/api/admin/pdf-forms/${creatorId}/${formId}`);
    return response.data;
  },

  getAdminQuizzesOverview: async () => {
    const response = await api.get('/api/admin/quizzes/overview');
    return response.data;
  },

  getAdminQuizzes: async (params = {}) => {
    const response = await api.get('/api/admin/quizzes', { params });
    return response.data;
  },

  getAdminQuizDetail: async (creatorId, quizId) => {
    const response = await api.get(`/api/admin/quizzes/${creatorId}/${quizId}`);
    return response.data;
  },

  // ============================================
  // BUSINESS VERIFICATION + CAPABILITIES
  // ============================================
  // Selling physical products needs an approved business verification AND an
  // active MintLink Plus subscription. `getMyCapabilities` is the single
  // source the whole UI gates on — never infer eligibility from the
  // verification status alone, since that misses the Plus half.

  getMyBusinessVerification: async () => {
    const response = await api.get('/api/creators/me/business-verification');
    return response.data;
  },

  saveBusinessVerificationDraft: async (data) => {
    const response = await api.put('/api/creators/me/business-verification', data);
    return response.data;
  },

  submitBusinessVerification: async () => {
    const response = await api.post('/api/creators/me/business-verification/submit');
    return response.data;
  },

  getMyCapabilities: async () => {
    const response = await api.get('/api/creators/me/capabilities');
    return response.data;
  },

  // ============================================
  // ADMIN — BUSINESS VERIFICATION
  // ============================================

  adminGetBusinessVerifications: async (params = {}) => {
    const response = await api.get('/api/admin/business-verifications', { params });
    return response.data;
  },

  adminGetBusinessVerificationStats: async () => {
    const response = await api.get('/api/admin/business-verifications/stats');
    return response.data;
  },

  adminGetBusinessVerification: async (creatorId) => {
    const response = await api.get(`/api/admin/business-verifications/${creatorId}`);
    return response.data;
  },

  adminReviewBusinessVerification: async (creatorId, body) => {
    const response = await api.post(`/api/admin/business-verifications/${creatorId}/review`, body);
    return response.data;
  },

  adminReviewVerificationRecheck: async (creatorId, body) => {
    const response = await api.post(`/api/admin/business-verifications/${creatorId}/recheck`, body);
    return response.data;
  },

  // ============================================
  // PHYSICAL COMMERCE — categories, variants, orders
  // ============================================
  // The category taxonomy drives which Legal Metrology declarations are
  // required. It is fetched rather than hardcoded so the form and the server
  // validator can never disagree about what's mandatory.

  getPhysicalCategories: async () => {
    const response = await api.get('/api/physical-categories');
    return response.data;
  },

  getGenericNameSuggestions: async (q) => {
    const response = await api.get('/api/physical-categories/generic-name-suggestions', { params: { q } });
    return response.data;
  },

  getMySellerDefaults: async () => {
    const response = await api.get('/api/creators/me/seller-defaults');
    return response.data;
  },

  saveMySellerDefaults: async (defaults) => {
    const response = await api.put('/api/creators/me/seller-defaults', defaults);
    return response.data;
  },

  // ============================================
  // ADMIN — PHYSICAL CATEGORIES
  // ============================================

  adminGetPhysicalCategories: async () => {
    const response = await api.get('/api/admin/physical-categories');
    return response.data;
  },

  adminCreatePhysicalCategory: async (body) => {
    const response = await api.post('/api/admin/physical-categories', body);
    return response.data;
  },

  adminUpdatePhysicalCategory: async (key, body) => {
    const response = await api.patch(`/api/admin/physical-categories/${key}`, body);
    return response.data;
  },

  // ============================================
  // DIGITAL CATEGORIES + SUBCATEGORIES
  // ============================================
  // Digital categories used to be a hardcoded array in src/utils/assetCategories.js,
  // duplicated three times across this app with the copies already drifted, and
  // never validated server-side. Now fetched, so adding a category or a
  // subcategory is an admin action rather than a deploy.

  getDigitalCategories: async () => {
    const response = await api.get('/api/digital-categories');
    return response.data;
  },

  adminGetDigitalCategories: async () => {
    const response = await api.get('/api/admin/digital-categories');
    return response.data;
  },

  adminCreateDigitalCategory: async (body) => {
    const response = await api.post('/api/admin/digital-categories', body);
    return response.data;
  },

  adminUpdateDigitalCategory: async (key, body) => {
    const response = await api.patch(`/api/admin/digital-categories/${key}`, body);
    return response.data;
  },

  adminCreateDigitalSubcategory: async (categoryKey, body) => {
    const response = await api.post(`/api/admin/digital-categories/${categoryKey}/subcategories`, body);
    return response.data;
  },

  // One comma-separated line in, a per-label report out. Never throws on a
  // duplicate — a paste where half the names already exist is the normal
  // case, and the report says which half.
  adminBulkCreateDigitalSubcategories: async (categoryKey, labels) => {
    const response = await api.post(
      `/api/admin/digital-categories/${categoryKey}/subcategories/bulk`,
      { labels },
    );
    return response.data;
  },

  adminUpdateDigitalSubcategory: async (categoryKey, subKey, body) => {
    const response = await api.patch(
      `/api/admin/digital-categories/${categoryKey}/subcategories/${subKey}`,
      body,
    );
    return response.data;
  },

  // ============================================
  // SUBCATEGORY REQUESTS
  // ============================================
  // A creator picks a category, sees its subcategories, and asks for one if
  // none fit. The product is never held back while that is decided — it has
  // a valid category already and a subcategory is optional.

  getMyCategoryRequests: async () => {
    const response = await api.get('/api/creators/me/category-requests');
    return response.data;
  },

  createCategoryRequest: async (body) => {
    const response = await api.post('/api/creators/me/category-requests', body);
    return response.data;
  },

  withdrawCategoryRequest: async (requestId) => {
    const response = await api.delete(`/api/creators/me/category-requests/${requestId}`);
    return response.data;
  },

  adminGetCategoryRequests: async () => {
    const response = await api.get('/api/admin/category-requests');
    return response.data;
  },

  adminReviewCategoryRequestGroup: async (body) => {
    const response = await api.post('/api/admin/category-requests/review', body);
    return response.data;
  },

  getAssetVariants: async (assetId) => {
    const response = await api.get(`/api/assets/${assetId}/variants`);
    return response.data;
  },

  replaceAssetVariants: async (assetId, body) => {
    const response = await api.put(`/api/assets/${assetId}/variants`, body);
    return response.data;
  },

  getPublicVariants: async (assetId) => {
    const response = await api.get(`/api/assets/${assetId}/variants/public`);
    return response.data;
  },

  getMyPhysicalOrders: async (params = {}) => {
    const response = await api.get('/api/creators/me/orders', { params });
    return response.data;
  },

  getMyPhysicalOrder: async (saleId) => {
    const response = await api.get(`/api/creators/me/orders/${saleId}`);
    return response.data;
  },

  createOrderShipment: async (saleId, body) => {
    const response = await api.post(`/api/creators/me/orders/${saleId}/shipment`, body);
    return response.data;
  },

  updateOrderStatus: async (saleId, body) => {
    const response = await api.patch(`/api/creators/me/orders/${saleId}/status`, body);
    return response.data;
  },

  cancelPhysicalOrder: async (saleId, body) => {
    const response = await api.post(`/api/creators/me/orders/${saleId}/cancel`, body);
    return response.data;
  },

  // Public: no auth. A buyer who just paid has no MintLink account.
  trackOrder: async (orderToken) => {
    const response = await api.get(`/api/orders/track/${orderToken}`);
    return response.data;
  },

  adminGetPhysicalOrders: async (params = {}) => {
    const response = await api.get('/api/admin/physical-orders', { params });
    return response.data;
  },

  adminGetPhysicalOrderStats: async () => {
    const response = await api.get('/api/admin/physical-orders/stats');
    return response.data;
  },

  adminGetPhysicalOrder: async (saleId) => {
    const response = await api.get(`/api/admin/physical-orders/${saleId}`);
    return response.data;
  },

  adminUpdateVerificationNotes: async (creatorId, internalNotes) => {
    const response = await api.patch(`/api/admin/business-verifications/${creatorId}/notes`, {
      internal_notes: internalNotes,
    });
    return response.data;
  },

  heartbeat: async () => {
    try { await api.post('/api/creators/me/heartbeat'); } catch (_) {}
  },
 async getPresignedUploadUrl(uploadRequest) {
    const response = await api.post('/api/uploads/presigned-url', uploadRequest);
    return response.data;
  },
 async createAssetFromUrls(assetData) {
    const response = await api.post('/api/assets', assetData);
    return response.data;
  },

  // Self-reports the outcome of the direct browser->S3 upload step — the one
  // part of an upload the backend can never see on its own. Best-effort: a
  // failure here must never surface as an error to the creator.
  reportUploadOutcome: async (report) => {
    try {
      await api.post('/api/creators/me/upload-log', report);
    } catch (_) { /* ignore */ }
  },

  // ============================================
  // SHAREABLE UPLOAD LINKS (public, token-authenticated — no login)
  // ============================================
  verifyUploadLink: async (token) => {
    const response = await api.get(`/api/upload-links/${token}`);
    return response.data;
  },

  getLinkPresignedUploadUrl: async (token, uploadRequest) => {
    const response = await api.post(`/api/upload-links/${token}/presigned-url`, uploadRequest);
    return response.data;
  },

  completeLinkUpload: async (token, assetData) => {
    const response = await api.post(`/api/upload-links/${token}/complete`, assetData);
    return response.data;
  },

  // Records that the caller's mobile number was proved by OTP. No body: the
  // server reads the proven number from the caller's (freshly refreshed)
  // Firebase token, so the client can't claim a number it didn't verify.
  verifyMyPhone: async () => {
    const response = await api.post('/api/creators/me/verify-phone');
    return response.data;
  },

  updateMyProfile: async (data) => {
    const response = await api.patch('/api/creators/me', data,{
      headers: {
        'Content-Type': 'multipart/form-data',
      }});
    return response.data;
  },

  getMyDashboard: async () => {try {
    const response = await api.get('/api/creators/me/dashboard');
    return response.data;}catch (error) {
    console.log(error);
    }
  },

  // ============================================
  // BANK DETAILS APIs
  // ============================================
  
  addBankDetails: async (data) => {
    const response = await api.post('/api/creators/me/bank-details', data);
    return response.data;
  },

  getMyBankDetails: async () => {
    const response = await api.get('/api/creators/me/bank-details');
    return response.data;
  },

  updateBankDetails: async (bankId, data) => {
    const response = await api.patch(`/api/creators/me/bank-details/${bankId}`, data);
    return response.data;
  },

  deleteBankDetails: async (bankId) => {
    const response = await api.delete(`/api/creators/me/bank-details/${bankId}`);
    return response.data;
  },

  // ============================================
  // ANALYTICS APIs
  // ============================================
  
  getMyAnalytics: async (days = 30) => {
    const response = await api.get('/api/creators/me/analytics', {
      params: { days }
    });
    return response.data;
  },

  getMyTrafficSources: async (days = 30) => {
    const response = await api.get('/api/creators/me/traffic-sources', {
      params: { days }
    });
    return response.data;
  },

  // ============================================
  // CONNECTED AI APPS (MintLink MCP)
  // ============================================

  // `params` is the AI app's OAuth query string, passed through untouched;
  // the backend validates it against the registered app, never this page.
  getMcpConsent: async (params) => {
    const response = await api.get('/api/mcp-oauth/authorize', { params });
    return response.data;
  },

  // `department` only for an admin connection (the admin MCP); creator
  // connections ignore it.
  approveMcpConnection: async (params, includeBuyerContact, department) => {
    const response = await api.post('/api/mcp-oauth/approve', {
      params, include_buyer_contact: includeBuyerContact, department,
    });
    return response.data;
  },

  denyMcpConnection: async (params) => {
    const response = await api.post('/api/mcp-oauth/deny', { params });
    return response.data;
  },

  getConnectedApps: async () => {
    const response = await api.get('/api/creators/me/connected-apps');
    return response.data;
  },

  disconnectApp: async (connectionId) => {
    const response = await api.delete(`/api/creators/me/connected-apps/${encodeURIComponent(connectionId)}`);
    return response.data;
  },

  // Your data: download + account deletion (backend app/routes/account.py)
  getAccountDeletion: async () => {
    const response = await api.get('/api/creators/me/deletion');
    return response.data;
  },

  requestAccountDeletion: async (confirm, reason) => {
    const response = await api.post('/api/creators/me/deletion', { confirm, reason: reason || null });
    return response.data;
  },

  cancelAccountDeletion: async () => {
    const response = await api.delete('/api/creators/me/deletion');
    return response.data;
  },

  exportMyData: async () => {
    const response = await api.post('/api/creators/me/data-export');
    return response.data;
  },

  // ============================================
  // PAYOUT APIs
  // ============================================
  
  getPayoutHistory: async (limit = 50) => {
    const response = await api.get('/api/creators/me/payouts', {
      params: { limit }
    });
    return response.data;
  },

  // ============================================
  // TRANSACTION APIs
  // ============================================
  
  // `search` matches the Order ID, the sale id, PayU's mihpayid, the bank
  // reference, the buyer's email/name/phone, or the product title — applied
  // server-side across the FULL history, not just the newest `limit` rows.
  // Everything a buyer or a bank statement might quote resolves here, which is
  // what keeps a payment query from becoming a support ticket.
  //
  // Also answers with `category_counts` and `category_labels` (digital /
  // physical / support / events / gig / review). Those counts are lifetime and
  // unwindowed, unlike the `limit`-capped list — see the note on statusCounts
  // in Transactions.jsx for what counting inside the window did wrong.
  getTransactionHistory: async (limit = 100, transactionType = null, search = null, category = null) => {
    const response = await api.get('/api/creators/me/transactions', {
      params: {
        limit,
        ...(transactionType && { transaction_type: transactionType }),
        ...(search && { search }),
        ...(category && { category }),
      }
    });
    return response.data;
  },

  // One order in the same detail an admin sees it: the fee breakdown component
  // by component, the per-download IP/area/device log, the payout clearing date,
  // PayU's own references, and a timeline. Fetched per order rather than folded
  // into the list — the download log is a filtered partition read each time.
  getSaleDetail: async (saleId) => {
    const response = await api.get(`/api/creators/me/sales/${saleId}`);
    return response.data;
  },

  resendDownloadLink: async (saleId) => {
    const response = await api.post(`/api/creators/me/sales/${saleId}/resend-download`);
    return response.data;
  },

  sendSaleInvoice: async (saleId) => {
    const response = await api.post(`/api/creators/me/sales/${saleId}/send-invoice`);
    return response.data;
  },

  // ============================================
  // ASSET APIs
  // ============================================
  
  createAsset: async (formData, onProgress) => {
    const response = await api.post('/api/assets', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: (progressEvent) => {
        if (onProgress) {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          onProgress(percentCompleted);
        }
      },
    });
    return response.data;
  },

  getAssetBySlug: async (slug) => {
    const response = await api.get(`/api/assets/${slug}`);
    return response.data;
  },

  getMyAssets: async (activeOnly = false) => {
    const response = await api.get('/api/creators/me/assets', {
      params: { active_only: activeOnly },
    });
    return response.data.assets || [];
  },

  // Opt-in paginated/filtered variant — kept separate from getMyAssets so its
  // many existing callers keep getting the full unpaginated list untouched.
  getMyAssetsPaged: async ({ activeOnly = true, productType, limit = 20, lastKey } = {}) => {
    const response = await api.get('/api/creators/me/assets', {
      params: {
        active_only: activeOnly,
        ...(productType ? { product_type: productType } : {}),
        limit,
        ...(lastKey ? { last_key: lastKey } : {}),
      },
    });
    return response.data; // { assets, last_key } — envelope kept so the caller can page further
  },

  updateAsset: async (assetId, updates) => {
    const response = await api.patch(`/api/assets/${assetId}`, updates);
    return response.data;
  },

  deleteAsset: async (assetId) => {
    const response = await api.delete(`/api/assets/${assetId}`);
    return response.data;
  },

  getMySales: async (limit = 50) => {
    const response = await api.get('/api/creators/me/sales', {
      params: { limit },
    });
    return response.data;
  },

  getMyOrders: async () => {
    const response = await api.get('/api/creators/me/orders');
    return response.data;
  },

  updateOrderFulfillment: async (saleId, data) => {
    const response = await api.patch(`/api/creators/me/orders/${saleId}`, data);
    return response.data;
  },

  // ============================================
  // PAYMENT APIs
  // ============================================
  
  // Fire-and-forget view ping for a public support page. Separate from
  // getSupportPage so link previews, crawlers and the creator's own editor
  // preview don't inflate the count. Never surfaced to the user on failure.
  trackSupportView: async (username) => {
    try { await api.post(`/api/supporters/${username}/track-view`); } catch { /* non-critical */ }
  },

  createPaymentOrder: async (data) => {
    const response = await api.post('/api/payments/create-order', data);
    return response.data;
  },

  // ============================================
  // DISCOUNT CODES — creator-issued product coupons + campaign attribution.
  // NB: unrelated to the referral methods elsewhere, which are about
  // recruiting other creators onto the platform.
  // ============================================

  // Buyer-side preview. Returns { valid: false, reason } for a bad code rather
  // than throwing — a wrong code is a normal checkout outcome, not an error.
  validateDiscountCode: async (payload) => {
    const response = await api.post('/api/discount-codes/validate', payload);
    return response.data;
  },

  getMyDiscountCodes: async () => {
    const response = await api.get('/api/creators/me/discount-codes');
    return response.data;
  },

  createDiscountCode: async (data) => {
    const response = await api.post('/api/creators/me/discount-codes', data);
    return response.data;
  },

  updateDiscountCode: async (code, data) => {
    const response = await api.patch(`/api/creators/me/discount-codes/${encodeURIComponent(code)}`, data);
    return response.data;
  },

  deleteDiscountCode: async (code) => {
    const response = await api.delete(`/api/creators/me/discount-codes/${encodeURIComponent(code)}`);
    return response.data;
  },

  getDiscountCodeSales: async (code) => {
    const response = await api.get(`/api/creators/me/discount-codes/${encodeURIComponent(code)}/sales`);
    return response.data;
  },

  // ============================================
  // SUPPORTERS — one-time appreciation payments
  // NB: distinct from the customer-support ticketing methods elsewhere in
  // this file. These live under /api/supporters/, not /api/support/.
  // ============================================

  // Public: everything the /support/:username page renders. No auth.
  getSupportPage: async (username) => {
    const response = await api.get(`/api/supporters/${username}`);
    return response.data;
  },

  getMySupportSettings: async () => {
    const response = await api.get('/api/creators/me/support-settings');
    return response.data;
  },

  updateMySupportSettings: async (settings) => {
    const response = await api.put('/api/creators/me/support-settings', settings);
    return response.data;
  },

  getMySupporters: async (limit = 50) => {
    const response = await api.get('/api/creators/me/supporters', { params: { limit } });
    return response.data;
  },

  // Creator moderation for the public wall — hiding a message never touches
  // the payment or the creator's earning.
  setSupportMessageHidden: async (saleId, hidden) => {
    const response = await api.patch(`/api/creators/me/supporters/${saleId}/message`, { hidden });
    return response.data;
  },

  verifyPayment: async (data) => {
    const response = await api.post('/api/payments/verify', data);
    return response.data;
  },

  // ============================================
  // DOWNLOAD APIs
  // ============================================
  
  verifyDownloadToken: async (token) => {
    const response = await api.get(`/api/downloads/${token}/verify`);
    return response.data;
  },

  // Locked-out buyer (expired link / downloads used up) asks the creator for a
  // fresh link. Works on an expired token. Returns { already_requested }.
  requestDownloadAccess: async (token, message) => {
    const response = await api.post(`/api/downloads/${token}/request-access`, { message: message || null });
    return response.data;
  },

  // Listing reports / IP takedowns. The first is public (the checkout page's
  // "Report this listing"); the rest are the reported creator's side.
  reportListing: (data) =>
    api.post('/api/listing-reports', data).then(r => r.data),
  getMyListingReports: () =>
    api.get('/api/creators/me/listing-reports').then(r => r.data),
  getMyListingReport: (reportId) =>
    api.get(`/api/creators/me/listing-reports/${reportId}`).then(r => r.data),
  submitCounterNotice: (reportId, data) =>
    api.post(`/api/creators/me/listing-reports/${reportId}/counter-notice`, data).then(r => r.data),

  getDownloadUrl: async (token) => {
    const response = await api.get(`/api/downloads/${token}/url`);
    return response.data;
  },
getRevenueForecast: async () => {
    const response = await api.get('/api/creators/me/forecast');
    return response.data;
  },
  
  // Called when a buyer visits the checkout page — counts as a "click".
  // `src` is the surface that sent them (see utils/navSource): passing it is
  // what lets a product view be credited to the marketplace rather than
  // disappearing into an undifferentiated total.
  trackAssetView: async (slug, src) => {
    const response = await api.post(`/api/assets/${slug}/track-view`, null, {
      params: src ? { src } : undefined,
    });
    return response.data;
  },

  // Called when an asset card enters the viewport in a listing — an
  // "impression". `surface` says WHICH listing: 'marketplace', 'creator',
  // 'page', 'product'. Without it the marketplace's own exposure is
  // indistinguishable from a creator page's, which is exactly how the
  // "Marketplace impressions" figure came to contain no marketplace data.
  trackAssetImpression: async (slug, surface) => {
    const response = await api.post(`/api/assets/${slug}/track-impression`, null, {
      params: surface ? { surface } : undefined,
    });
    return response.data;
  },

  // ── Marketplace telemetry ────────────────────────────────────────────────
  // The grid's own events, as opposed to a single asset's. Both are
  // fire-and-forget: every caller must .catch(() => {}) so a counter can never
  // block the navigation it is describing.
  trackMarketplaceEvent: async (event, extra = {}) => {
    const response = await api.post('/api/marketplace/track', { event, ...extra });
    return response.data;
  },

  trackMarketplaceSearch: async (query, resultCount) => {
    const response = await api.post('/api/marketplace/track-search', {
      query,
      result_count: resultCount,
    });
    return response.data;
  },

  // Top sellers by real sales_count, no rating — see the endpoint's own
  // docstring for why: 0 of 1,442 active products carry a review, so a star
  // score on this widget would be fabricated.
  getMarketplaceTopCreators: async (limit = 6) => {
    const response = await api.get('/api/marketplace/top-creators', { params: { limit } });
    return response.data;
  },

  // Real search terms only. Returns { queries: [], sufficient_data: false }
  // below a minimum volume — the caller's job is to fall back to a static
  // "Popular categories" list rather than label three real terms "Trending".
  getMarketplaceTrendingSearches: async (limit = 8) => {
    const response = await api.get('/api/marketplace/trending-searches', { params: { limit } });
    return response.data;
  },

  // ── Admin: marketplace console ───────────────────────────────────────────
  getAdminMarketplaceOverview: async (days = 30) => {
    const response = await api.get('/api/admin/marketplace/overview', { params: { days } });
    return response.data;
  },

  getAdminMarketplaceRanking: async (limit = 24) => {
    const response = await api.get('/api/admin/marketplace/ranking', { params: { limit } });
    return response.data;
  },

  getAssetStats: async (slug) => {
    const response = await api.get(`/api/assets/${slug}/stats`);
    return response.data;
  },
  
  bulkAssetOperation: async (assetIds, operation) => {
    const response = await api.post('/api/assets/bulk', {
      asset_ids: assetIds,
      operation
    });
    return response.data;
  },
  markAsDownloaded: async (token, clientIp) => {
    const response = await api.post(`/api/downloads/${token}/mark-downloaded`, clientIp ? { client_ip: clientIp } : {});
    return response.data;
  },
  getAllCreators: async (params = {}) => {
    const response = await api.get('/api/creators', { params });
    return response.data;
  },

  getMarketplaceAssets: async (params = {}) => {
    const response = await api.get('/api/assets/marketplace', { params });
    return response.data;
  },

  getCreatorByUsername: async (username) => {
    const response = await api.get(`/api/creators/${username}/profile`);
    return response.data;
  },
getPayoutEligibility: async () => {
    const response = await api.get('/api/creators/me/payout-eligibility');
    return response.data;
  },
  getCreatorAssets: async (username) => {
    const response = await api.get(`/api/creators/${username}/assets`);
    return response.data;
  },
  getPublicCreatorEvents: async (username) => {
    const response = await api.get(`/api/creators/${username}/events`);
    return response.data;
  },
  // The fifth public by-username listing. Gigs were the one product line a
  // visitor could not ask about without going through the marketplace.
  getPublicCreatorGigs: async (username) => {
    const response = await api.get(`/api/creators/${username}/gigs`);
    return response.data;
  },

  getPublicCreatorServices: async (username) => {
    const response = await api.get(`/api/creators/${username}/services`);
    return response.data;
  },

  // Check if a username handle is available (returns { available: bool })
  checkUsernameAvailability: async (username) => {
    const response = await api.get('/api/creators/check-username', { params: { username } });
    return response.data;
  },

  // Fire-and-forget: increment view count for a creator's public profile
  trackCreatorProfileView: async (username, extra = {}) => {
    // Attribution rides along with the view it describes, so a visit and where
    // it came from are always recorded together or not at all.
    const response = await api.post(`/api/creators/${username}/track-view`, {
      ...getAttribution(),
      ...extra,
    });
    return response.data;
  },

  // Fire-and-forget: track a click on a social link from a creator's profile
  trackSocialLinkClick: async (username, platform) => {
    const response = await api.post(`/api/creators/${username}/track-social-click`, { platform });
    return response.data;
  },

  // Fire-and-forget: one engagement event against a dashboard feature card or
  // an announcement. featureId is "storefront" / "events" / ... for a product
  // card, or "announcement:{id}" for a poster/banner's view or cta_click —
  // see app/routes/feature_usage.py for why the two share one endpoint.
  // Every call site should .catch(() => {}) this; tracking must never be able
  // to block the click or navigation it's describing.
  trackFeatureEvent: async (featureId, eventType) => {
    const response = await api.post('/api/features/track', { feature_id: featureId, event_type: eventType });
    return response.data;
  },

  // Profile-level analytics: views, social clicks, marketplace impressions
  getCreatorProfileAnalytics: async (days = 30) => {
    const response = await api.get('/api/creators/me/profile-analytics', { params: { days } });
    return response.data;
  },

  // ============================================
  // SUPPORT TICKET APIs (Customer)
  // ============================================
  
  createSupportTicket: async (data) => {
    const response = await api.post('/api/support/tickets', data);
    return response.data;
  },

  getMyTickets: async (params = {}) => {
    const response = await api.get('/api/support/tickets/my', { params });
    return response.data;
  },

  getSupportUnreadCount: async () => {
    const response = await api.get('/api/support/tickets/unread-count');
    return response.data;
  },

  // `accessToken` is the secret link token a guest ticket is opened with
  // (returned at creation and put in the confirmation email). Logged-in
  // owners and admins don't need it.
  getTicketDetails: async (ticketId, accessToken) => {
    const response = await api.get(`/api/support/tickets/${ticketId}`, {
      params: accessToken ? { t: accessToken } : undefined,
    });
    return response.data;
  },

  addTicketMessage: async (ticketId, data) => {
    const response = await api.post(`/api/support/tickets/${ticketId}/messages`, data);
    return response.data;
  },

  uploadSupportAttachment: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post('/api/support/attachments', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },
  getNotifications: (params) => 
    api.get('/notifications/my', { params }),
  
  // Get unread count
  getUnreadCount: () => 
    api.get('/notifications/unread-count'),
  
  // Mark notification as read
  markNotificationRead: (notificationId) =>
    api.patch(`/notifications/${notificationId}`, {
      status: 'read'
    }),
  
  // Mark all as read
  markAllRead: () =>
    api.post('/notifications/mark-all-read'),
  
  // Delete notification
  deleteNotification: (notificationId) =>
    api.delete(`/notifications/${notificationId}`),

  // ── Push notifications (FCM) ──────────────────────────────────
  subscribeToPush: (token, platform = 'web') =>
    api.post('/api/push/subscribe', { token, platform }).then(r => r.data),

  unsubscribeFromPush: (token) =>
    api.delete('/api/push/unsubscribe', { params: { token } }).then(r => r.data),

  // Confirms a push actually reached this device (or was opened). FCM only
  // tells the server it ACCEPTED a message; whether a device ever ran the
  // handler is something only the device knows. Unauthenticated by design —
  // push_id is the capability. The service worker posts the same shape
  // directly (it has no axios), so keep the two in step.
  ackPush: (pushId, event) =>
    api.post('/api/push/ack', { push_id: pushId, event }).then(r => r.data),

  // ── Announcements (product news) ──────────────────────────────
  // Active, non-expired, audience-matched announcements for the current user.
  getActiveAnnouncements: () =>
    api.get('/api/announcements/active').then(r => r.data),

  // Dismiss an announcement for the current user (syncs across devices).
  dismissAnnouncement: (announcementId) =>
    api.post(`/api/announcements/${announcementId}/dismiss`),

  // ============================================
  // BUYER APIs
  // ============================================

  // Exchange Firebase ID token for a buyer session token
  buyerLogin: async (data) => {
    const response = await api.post('/api/buyers/login', data);
    return response.data;
  },

  // Email-OTP login (alternative to phone) — send code, then verify to
  // receive a buyer_token directly (no Firebase involved on this path).
  sendBuyerEmailOtp: async (email) => {
    const response = await api.post('/api/buyers/login-email/send-otp', { email });
    return response.data;
  },

  verifyBuyerEmailOtp: async (email, otp) => {
    const response = await api.post('/api/buyers/login-email/verify', { email, otp });
    return response.data;
  },

  // Get buyer's name, email, and latest shipping address (for pre-filling checkout)
  getBuyerProfile: async (buyerToken) => {
    const response = await api.get('/api/buyers/me/profile', {
      headers: { 'X-Buyer-Token': buyerToken },
    });
    return response.data;
  },

  // Get all purchases for the authenticated buyer (uses buyer_token header)
  getBuyerPurchases: async () => {
    const token = safeSessionStorage.getItem('buyer_token');
    const response = await api.get('/api/buyers/me/purchases', {
      headers: token ? { 'X-Buyer-Token': token } : {},
    });
    return response.data;
  },

  // Get all initiated/failed payment attempts for this buyer (not yet successful)
  getBuyerPendingPayments: async () => {
    const token = safeSessionStorage.getItem('buyer_token');
    const response = await api.get('/api/buyers/me/payment-attempts', {
      headers: token ? { 'X-Buyer-Token': token } : {},
    });
    return response.data;
  },

  // Get a secure stream URL for a preview-mode asset (by sale_id)
  getBuyerPreviewUrl: async (saleId, fileType) => {
    const token = safeSessionStorage.getItem('buyer_token');
    const response = await api.get(`/api/buyers/me/purchases/${saleId}/preview`, {
      headers: token ? { 'X-Buyer-Token': token } : {},
      responseType: 'blob',
      timeout: 300000, // full-length preview stream, not a JSON call — allow up to 5 min
    });
    return { stream_url: makeBlobUrl(response.data, response.headers['content-type'], fileType) };
  },

  // Fetch the streamed binary via axios (so custom headers like ngrok-skip-browser-warning
  // are sent), then convert to a local Blob URL for use as src in SecureViewer.
  getPreviewUrl: async (downloadToken, fileType) => {
    const response = await api.get(`/api/downloads/${downloadToken}/preview`, {
      responseType: 'blob',
      timeout: 300000, // full-length preview stream, not a JSON call — allow up to 5 min
    });
    return { stream_url: makeBlobUrl(response.data, response.headers['content-type'], fileType) };
  },

  // ============================================
  // CREATOR-AS-BUYER APIs (authenticated via Firebase)
  // ============================================

  // Get purchases made BY the creator (as a buyer) — authenticated via Firebase token
  getCreatorPurchases: async () => {
    const response = await api.get('/api/creators/me/purchases');
    return response.data;
  },

  // Get secure preview URL for a creator's purchase (by sale_id)
  getCreatorPreviewUrl: async (saleId, fileType) => {
    const response = await api.get(`/api/creators/me/purchases/${saleId}/preview`, {
      responseType: 'blob',
      timeout: 300000, // full-length preview stream, not a JSON call — allow up to 5 min
    });
    return { stream_url: makeBlobUrl(response.data, response.headers['content-type'], fileType) };
  },

  // ============================================
  // REVIEW APIs
  // ============================================

  // Ratings need no login: the purchase's download token is the credential,
  // and the backend allows one rating per Order ID. 409 = already rated.
  getRatingContext: async (reviewToken) =>
    (await api.get(`/api/reviews/by-token/${encodeURIComponent(reviewToken)}`)).data,
  submitReview: async (reviewToken, { rating, comment }) =>
    (await api.post(`/api/reviews/by-token/${encodeURIComponent(reviewToken)}`, { rating, comment })).data,
  // Stops rating request emails to this purchase's buyer email.
  unsubscribeRatingEmails: async (reviewToken) =>
    (await api.post(`/api/reviews/by-token/${encodeURIComponent(reviewToken)}/unsubscribe`)).data,

  // Fetch public reviews for a product (no auth)
  getAssetReviews: async (slug, { limit = 20, lastKey } = {}) => {
    const response = await api.get(`/api/assets/${slug}/reviews`, {
      params: { limit, ...(lastKey ? { last_key: lastKey } : {}) },
    });
    return response.data;
  },

  // Creator replies to a review (Firebase auth via interceptor)
  replyToReview: async (reviewId, reply) => {
    const response = await api.post(`/api/reviews/${reviewId}/reply`, { reply });
    return response.data;
  },

  // Creator reports a review to MintLink (Firebase auth). Creators can't hide
  // reviews themselves; an admin decides from /admin/review-reports.
  reportReview: async (reviewId, reason) => {
    const response = await api.post(`/api/reviews/${reviewId}/report`, { reason });
    return response.data;
  },

  // Fetch all reviews across a creator's products (Firebase auth)
  getMyReviews: async () => {
    const response = await api.get('/api/creators/me/reviews');
    return response.data;
  },

  // ============================================
  // PUBLIC BLOG APIs (no auth required)
  // ============================================

  getPublishedBlogs: async (params = {}) => {
    const response = await api.get('/api/blogs', { params });
    return response.data;
  },

  getBlogBySlug: async (slug) => {
    const response = await api.get(`/api/blogs/${slug}`);
    return response.data;
  },

  // ============================================
  // REFERRAL APIs
  // ============================================

  // No auth — call on landing with a ref code in the URL
  trackReferralClick: async (data) => {
    const response = await api.post('/api/referrals/track-click', data);
    return response.data;
  },

  // Auth required — creator's referral code, share links, and stats
  getMyReferral: async () => {
    const response = await api.get('/api/creators/me/referral');
    return response.data;
  },

  // ============================================
  // EMAIL OTP VERIFICATION
  // ============================================

  sendEmailOtp: async (email) => {
    const response = await api.post('/api/auth/send-email-otp', { email });
    return response.data;
  },

  verifyEmailOtp: async (email, otp) => {
    const response = await api.post('/api/auth/verify-email-otp', { email, otp });
    return response.data;
  },

  // ============================================
  // EVENTS / BOOKINGS APIs
  // ============================================

  // Creator-owned events
  getMyEvents: async () => {
    const response = await api.get('/api/creators/me/events');
    return response.data.events || [];
  },
  getMyEvent: async (eventId) => {
    const response = await api.get(`/api/creators/me/events/${eventId}`);
    return response.data;
  },
  createEvent: async (data) => {
    const response = await api.post('/api/creators/me/events', data);
    return response.data;
  },
  updateEvent: async (eventId, data) => {
    const response = await api.patch(`/api/creators/me/events/${eventId}`, data);
    return response.data;
  },
  deleteEvent: async (eventId) => {
    const response = await api.delete(`/api/creators/me/events/${eventId}`);
    return response.data;
  },
  getEventAttendees: async (eventId) => {
    const response = await api.get(`/api/creators/me/events/${eventId}/attendees`);
    return response.data;
  },
  getCancellationPreview: async (eventId) => {
    const response = await api.get(`/api/creators/me/events/${eventId}/cancellation-preview`);
    return response.data;
  },
  requestEventCancellation: async (eventId, reason) => {
    const response = await api.post(`/api/creators/me/events/${eventId}/cancel-request`, { reason });
    return response.data;
  },
  setEventBookingStatus: async (eventId, closed) => {
    const response = await api.post(`/api/creators/me/events/${eventId}/booking-status`, { closed });
    return response.data;
  },
  // body: { ticket_token?, ticket_code?, undo? }
  checkinAttendee: async (eventId, body) => {
    const response = await api.post(`/api/creators/me/events/${eventId}/checkin`, body);
    return response.data;
  },

  // Public event + booking
  getPublicEvent: async (slug) => {
    const response = await api.get(`/api/events/${slug}`);
    return response.data;
  },
  // body: { tier_id, quantity, buyer_name, buyer_email, buyer_phone, form_data }
  // → free: { status:'confirmed', ticket_token, ... }; paid: { status:'payment_required', payment_params, ... }
  createBooking: async (slug, body) => {
    const response = await api.post(`/api/events/${slug}/book`, body);
    return response.data;
  },
  getTicket: async (token) => {
    const response = await api.get(`/api/tickets/${token}`);
    return response.data;
  },
  resendTicket: async (token) => {
    const response = await api.post(`/api/tickets/${token}/resend`);
    return response.data;
  },

  // ============================================
  // PAGES / TEMPLATES APIs (Link-in-bio page builder)
  // ============================================

  // List the authenticated creator's pages → { pages: [...] }
  getMyPages: async () => {
    const response = await api.get('/api/creators/me/pages');
    return response.data;
  },

  // ── MintLink Plus storefront (/o/:username) ────────────────────────────────
  // One per creator, so there is no id in any of these paths.
  getMyStorefront: async () => {
    const response = await api.get('/api/creators/me/storefront');
    return response.data;
  },

  // Partial saves are supported: omit `sections` or `theme` to leave the
  // stored value untouched (the editor's autosave relies on this).
  saveMyStorefront: async (body) => {
    const response = await api.put('/api/creators/me/storefront', body);
    return response.data;
  },

  deleteMyStorefront: async () => {
    const response = await api.delete('/api/creators/me/storefront');
    return response.data;
  },

  // ── Pages ──────────────────────────────────────────────────────────────────
  // A storefront is a website with many pages, one row each. These write ONE
  // page: the editor's autosave goes through saveStorefrontPage rather than
  // saveMyStorefront so that typing on the About page never rewrites the
  // whole site. Each returns the full refreshed `pages` list, so the builder
  // never has to guess what the server now holds.
  createStorefrontPage: async (body) => {
    const response = await api.post('/api/creators/me/storefront/pages', body);
    return response.data;
  },

  saveStorefrontPage: async (pageId, body) => {
    const response = await api.put(`/api/creators/me/storefront/pages/${pageId}`, body);
    return response.data;
  },

  deleteStorefrontPage: async (pageId) => {
    const response = await api.delete(`/api/creators/me/storefront/pages/${pageId}`);
    return response.data;
  },

  // Sends ids only — moving a page in a list must not cost an upload of every
  // page's sections.
  reorderStorefrontPages: async (pageIds) => {
    const response = await api.put('/api/creators/me/storefront/pages/order', { page_ids: pageIds });
    return response.data;
  },

  getPublicStorefront: async (username) => {
    const response = await api.get(`/api/storefront/${username}`);
    return response.data;
  },

  // Public — the storefront's email-capture section posts here.
  subscribeToStorefront: async (username, email, source = 'storefront') => {
    const response = await api.post(`/api/storefront/${username}/subscribe`, { email, source });
    return response.data;
  },

  getMyStorefrontSubscribers: async () => {
    const response = await api.get('/api/creators/me/storefront/subscribers');
    return response.data;
  },

  // Public — the storefront's Contact section posts here.
  submitStorefrontContact: async (username, body) => {
    const response = await api.post(`/api/storefront/${username}/contact`, body);
    return response.data;
  },

  getMyStorefrontContactMessages: async () => {
    const response = await api.get('/api/creators/me/storefront/contact-messages');
    return response.data;
  },

  // ── Custom domains ─────────────────────────────────────────────────────────
  // One per creator. The GET is also the status poll while DNS propagates —
  // it asks Vercel live rather than trusting the stored status.
  getMyDomain: async () => {
    const response = await api.get('/api/creators/me/domain');
    return response.data;
  },

  addMyDomain: async (domain) => {
    const response = await api.post('/api/creators/me/domain', { domain });
    return response.data;
  },

  removeMyDomain: async () => {
    const response = await api.delete('/api/creators/me/domain');
    return response.data;
  },

  // Get a single owned page incl. blocks
  getMyPage: async (pageId) => {
    const response = await api.get(`/api/creators/me/pages/${pageId}`);
    return response.data;
  },

  // Create a page; body { title, slug?, template_id? } (clones template blocks/theme if given)
  createPage: async (data) => {
    const response = await api.post('/api/creators/me/pages', data);
    return response.data;
  },

  // Bulk save a whole page: { title, slug, theme, is_published, blocks: [...] }
  savePage: async (pageId, data) => {
    const response = await api.patch(`/api/creators/me/pages/${pageId}`, data);
    return response.data;
  },

  deletePage: async (pageId) => {
    const response = await api.delete(`/api/creators/me/pages/${pageId}`);
    return response.data;
  },

  // Choose which page is shared at the bare /c/:username URL
  setPrimaryPage: async (pageId) => {
    const response = await api.post(`/api/creators/me/pages/${pageId}/set-primary`);
    return response.data;
  },

  // Public: fetch a specific page by username + slug → { page, creator }
  getPublicPage: async (username, pageSlug) => {
    const response = await api.get(`/api/creators/${username}/pages/${pageSlug}`);
    return response.data;
  },

  // Public: lean list of a creator's published pages → { pages: [...] }
  // Used to show "browse this creator's other pages" when a page link 404s.
  getPublicPages: async (username) => {
    const response = await api.get(`/api/creators/${username}/pages`);
    return response.data;
  },

  // Fire-and-forget: track a click on a custom link block (mirrors track-social-click)
  trackPageLinkClick: async (username, blockId) => {
    const response = await api.post(`/api/creators/${username}/track-link-click`, { block_id: blockId });
    return response.data;
  },

  // Platform templates (public) → { templates: [...] }
  listTemplates: async () => {
    const response = await api.get('/api/templates');
    return response.data;
  },

  // The creator's own saved templates → { templates: [...] }
  listMyTemplates: async () => {
    const response = await api.get('/api/creators/me/templates');
    return response.data;
  },

  // Save an existing page as a personal template; body { page_id, name }
  saveAsTemplate: async (data) => {
    const response = await api.post('/api/creators/me/templates', data);
    return response.data;
  },

  deleteTemplate: async (templateId) => {
    const response = await api.delete(`/api/creators/me/templates/${templateId}`);
    return response.data;
  },

  // ============================================
  // INSTAGRAM AUTO-REPLY (AutoDM) APIs
  // ============================================

  // Connection status → { connected, account (the first account, legacy shape),
  //   accounts: [{ ig_user_id, ig_username, ig_profile_picture, followers_count,
  //     is_primary, active, slot_id, expires_at }],
  //   addon: { slots: [{ slot_id, expires_at, active, ig_user_id }], free_slots, can_add_more } }
  getInstagramStatus: async () => {
    const response = await api.get('/api/instagram/status');
    return response.data;
  },

  // Begin OAuth → { oauth_url }. Redirect the browser to oauth_url to connect.
  // `add: true` = connecting an ADDITIONAL account: 402 if there's no paid
  // slot for it, and Instagram is asked to show its login screen again so a
  // different account can be picked.
  getInstagramConnectUrl: async ({ add = false } = {}) => {
    const response = await api.get('/api/instagram/connect', { params: add ? { add: true } : undefined });
    return response.data;
  },

  // Omit igUserId to disconnect the first account.
  disconnectInstagram: async (igUserId) => {
    const response = await api.delete('/api/instagram/disconnect',
      { params: igUserId ? { ig_user_id: igUserId } : undefined });
    return response.data;
  },

  // List auto-reply rules → { rules: [...] }
  listInstagramRules: async () => {
    const response = await api.get('/api/instagram/rules');
    return response.data;
  },

  // Create a rule; body { keyword, message, post_id? } → created rule (201)
  createInstagramRule: async (data) => {
    const response = await api.post('/api/instagram/rules', data);
    return response.data;
  },

  // Edit a rule; same body as create → { message, rule_id }
  updateInstagramRule: async (ruleId, data) => {
    const response = await api.patch(`/api/instagram/rules/${ruleId}`, data);
    return response.data;
  },

  // Toggle a rule on/off (no body) → { rule_id, is_active }
  toggleInstagramRule: async (ruleId) => {
    const response = await api.patch(`/api/instagram/rules/${ruleId}/toggle`);
    return response.data;
  },

  deleteInstagramRule: async (ruleId) => {
    const response = await api.delete(`/api/instagram/rules/${ruleId}`);
    return response.data;
  },

  // Omit igUserId for the first account's posts. → { media, next_cursor }:
  // pass next_cursor back as `after` for the next page, or `ids` (array) to
  // fetch specific posts instead of a page.
  getInstagramMedia: async (igUserId, { after, ids } = {}) => {
    const params = {
      ...(igUserId ? { ig_user_id: igUserId } : {}),
      ...(after ? { after } : {}),
      ...(ids ? { ids: ids.join(',') } : {}),
    };
    const response = await api.get('/api/instagram/media',
      { params: Object.keys(params).length ? params : undefined });
    return response.data;
  },

  // Access/plan bundle → { has_access, is_pilot, is_paid, rule_limit,
  //   active_rule_count, unrestricted_links, subscription, price_display }
  getInstagramAutodmAccess: async () => {
    const response = await api.get('/api/instagram/access');
    return response.data;
  },

  // ============================================
  // AUTO DM SUBSCRIPTION (₹199/month + 18% GST, unlocks custom links; rules are free)
  // ============================================

  getMyAutodmSubscription: async () => {
    const response = await api.get('/api/creators/me/autodm');
    return response.data;
  },

  // Same create-order contract as Plus — hand the result to startPayment().
  // `extraAccounts` > 0 adds that many extra-account slots to the same payment.
  // `autorenew` also sets up a Razorpay mandate renewing plan + accounts monthly.
  createAutodmOrder: async (plan = 'autodm_monthly', extraAccounts = 0, autorenew = false) => {
    const body = { plan, autorenew: Boolean(autorenew) };
    if (extraAccounts > 0) body.extra_accounts = extraAccounts;
    const response = await api.post('/api/autodm/create-order', body);
    return response.data;
  },

  // Auto-renew on an already-paid plan (or redo it after adding/removing an
  // account): returns a mandate to approve via startPayment(), first charge
  // just before the current period ends. → { first_charge_at, amount, ... }
  enableAutodmAutorenew: async () => {
    const response = await api.post('/api/creators/me/autodm/autorenew');
    return response.data;
  },

  disableAutodmAutorenew: async () => {
    const response = await api.post('/api/creators/me/autodm/autorenew/cancel');
    return response.data;
  },

  // Extra-account add-on (₹129 + GST per account per 30 days). No slotId = buy
  // a new slot; with one = add 30 days to it. Same PayU contract as above.
  createAutodmAddonOrder: async (slotId) => {
    const response = await api.post('/api/autodm/addon/create-order', slotId ? { slot_id: slotId } : {});
    return response.data;
  },

  cancelAutodmSubscription: async () => {
    const response = await api.post('/api/creators/me/autodm/cancel');
    return response.data;
  },

  // GST tax invoices, one per payment → { invoices: [{ invoice_id, invoice_number,
  //   invoice_date, amount, taxable_value, gst, period_start, period_end }] }
  getMyAutodmInvoices: async () => {
    const response = await api.get('/api/creators/me/autodm/invoices');
    return response.data;
  },

  getMyAutodmInvoiceHtml: async (invoiceId) => {
    const response = await api.get(`/api/creators/me/autodm/invoices/${encodeURIComponent(invoiceId)}`,
      { responseType: 'text' });
    return response.data;
  },

  resumeAutodmSubscription: async () => {
    const response = await api.post('/api/creators/me/autodm/resume');
    return response.data;
  },

  // ============================================
  // MINTLINK PLUS (subscription) APIs
  // ============================================

  // Current subscription → { status: 'none'|'active'|'cancelled'|'expired',
  //   plan, price, started_at, renews_at, expires_at }
  getMyPlusSubscription: async () => {
    const response = await api.get('/api/creators/me/plus');
    return response.data;
  },

  // Start a Plus purchase — hand the result to startPayment(). `autorenew`
  // also sets up a Razorpay mandate that renews Plus monthly.
  createPlusOrder: async (plan = 'plus_monthly', autorenew = false) => {
    const response = await api.post('/api/plus/create-order', { plan, autorenew: Boolean(autorenew) });
    return response.data;
  },

  // Auto-renew on an already-paid Plus → a mandate to approve via startPayment().
  enablePlusAutorenew: async () => {
    const response = await api.post('/api/creators/me/plus/autorenew');
    return response.data;
  },

  disablePlusAutorenew: async () => {
    const response = await api.post('/api/creators/me/plus/autorenew/cancel');
    return response.data;
  },

  // Cancel at period end (creator keeps Plus until expires_at)
  cancelPlusSubscription: async () => {
    const response = await api.post('/api/creators/me/plus/cancel');
    return response.data;
  },

  // Undo a pending cancellation
  resumePlusSubscription: async () => {
    const response = await api.post('/api/creators/me/plus/resume');
    return response.data;
  },

  // Billing history → { invoices: [{ invoice_id, amount, paid_at, period_start, period_end, invoice_url? }] }
  getPlusInvoices: async () => {
    const response = await api.get('/api/creators/me/plus/invoices');
    return response.data;
  },

  // ============================================
  // MEMBERSHIPS (fan subscriptions) APIs
  // ============================================
  //
  // A membership does NOT auto-renew. MintLink holds no PayU mandate, so a
  // period is bought one payment at a time and the member renews by tapping a
  // link. Every payload from these endpoints carries `auto_renews: false` —
  // render it, don't hide it, and never write copy that implies otherwise.

  // The creator's own tiers, with member counts and lifetime revenue.
  getMyMembershipPlans: async () => {
    const response = await api.get('/api/memberships/plans');
    return response.data;
  },

  createMembershipPlan: async (plan) => {
    const response = await api.post('/api/memberships/plans', plan);
    return response.data;
  },

  // `confirmRemovals` is required when the save removes a class somebody has
  // already booked — the server refuses with 409 and the details until then.
  updateMembershipPlan: async (planId, plan, confirmRemovals = false) => {
    const response = await api.put(`/api/memberships/plans/${planId}`, plan, {
      params: confirmRemovals ? { confirm_removals: true } : {},
    });
    return response.data;
  },

  // Deletes only if nobody ever joined; otherwise unpublishes and says so in
  // the response ({ status: 'unpublished', reason: 'members_exist', ... }).
  deleteMembershipPlan: async (planId) => {
    const response = await api.delete(`/api/memberships/plans/${planId}`);
    return response.data;
  },

  getMembershipPlanMembers: async (planId) => {
    const response = await api.get(`/api/memberships/plans/${planId}/members`);
    return response.data;
  },

  // Public — a creator's published tiers, for the join page.
  getPublicMembershipPlans: async (username) => {
    const response = await api.get(`/api/creators/${username}/membership-plans`);
    return response.data;
  },

  // Public — start a join. Returns a PayU form-post payload, plus
  // status:'renewal' when this person already holds the membership.
  joinMembership: async (payload) => {
    const response = await api.post('/api/memberships/join', payload);
    return response.data;
  },

  // The member's own card, by the token in their link. Includes
  // member_content only while the membership is active.
  getMemberCard: async (token) => {
    const response = await api.get(`/api/memberships/card/${token}`);
    return response.data;
  },

  // Start a renewal payment for an existing membership (works when lapsed).
  renewMembership: async (token) => {
    const response = await api.post(`/api/memberships/card/${token}/renew`);
    return response.data;
  },

  // Stop the reminders. Access is NOT revoked — the member keeps every day
  // they paid for, and the response says until when.
  cancelMembership: async (token) => {
    const response = await api.post(`/api/memberships/card/${token}/cancel`);
    return response.data;
  },

  resumeMembership: async (token) => {
    const response = await api.post(`/api/memberships/card/${token}/resume`);
    return response.data;
  },

  // Pause / unpause is NOT cancel / resume. Cancel stops renewal reminders and
  // never touches access; pause stops ACCESS and the clock with it, holding the
  // member's remaining days until they come back.
  pauseMembership: async (token) => {
    const response = await api.post(`/api/memberships/card/${token}/pause`);
    return response.data;
  },

  unpauseMembership: async (token) => {
    const response = await api.post(`/api/memberships/card/${token}/unpause`);
    return response.data;
  },

  // A member's own attendance history, by their pass token.
  getMemberVisits: async (token) => {
    const response = await api.get(`/api/memberships/card/${token}/visits`);
    return response.data;
  },

  // The door. `code` may be a scanned QR payload (a full member URL or the raw
  // token) or the eight-character code read aloud — the server resolves either.
  //
  // Never returns 4xx for a refused member: "not allowed in" is a normal answer
  // at a door and comes back as { admitted: false, reason } so the desk can see
  // WHO was refused and why, not a generic error toast.
  checkInMember: async (code) => {
    // Sends the desk token when the device has one; the server falls back to
    // the creator's own session otherwise, so an owner scanning on their own
    // phone needs no setup.
    const staff = apiService.getStaffToken();
    const response = await api.post('/api/memberships/check-in', { code },
      staff ? { headers: { 'X-Staff-Token': staff } } : undefined);
    return response.data;
  },

  getRecentCheckIns: async (limit = 50) => {
    const staff = apiService.getStaffToken();
    const response = await api.get('/api/memberships/check-ins', {
      params: { limit },
      ...(staff ? { headers: { 'X-Staff-Token': staff } } : {}),
    });
    return response.data;
  },

  // ── Class booking ────────────────────────────────────────────────────────
  //
  // The timetable is a weekly template on the plan; an occurrence only becomes
  // a stored row once somebody books it. Both halves of the key (slot_id and
  // start_at) go to the server and both are validated there.

  getMemberClasses: async (token, days = 14) => {
    const response = await api.get(`/api/memberships/card/${token}/classes`, { params: { days } });
    return response.data;
  },

  // On a metered plan this spends a credit at BOOKING time, not on arrival —
  // the held place is the scarce thing.
  bookMemberClass: async (token, slotId, startAt) => {
    const response = await api.post(`/api/memberships/card/${token}/book`, {
      slot_id: slotId, start_at: startAt,
    });
    return response.data;
  },

  // Whether the credit comes back depends on the plan's cutoff; the response
  // says which happened rather than leaving the UI to guess.
  cancelMemberClass: async (token, slotId, startAt) => {
    const response = await api.post(`/api/memberships/card/${token}/cancel-booking`, {
      slot_id: slotId, start_at: startAt,
    });
    return response.data;
  },

  // Creator: who is coming to each class.
  getClassRoster: async (planId, days = 7) => {
    const response = await api.get(`/api/memberships/plans/${planId}/roster`, { params: { days } });
    return response.data;
  },

  // ── Retention ────────────────────────────────────────────────────────────
  //
  // The headline is renewal conversion, not MRR. Nothing renews itself here,
  // so any "recurring revenue" framing would be untrue — see the backend
  // analytics module.
  getMembershipAnalytics: async (windowDays = 30) => {
    const response = await api.get('/api/memberships/analytics', {
      params: { window_days: windowDays },
    });
    return response.data;
  },

  // The full money waterfall for one payment: what the member pays, the
  // creator's own GST passing through, MintLink's four fee lines, and what the
  // creator is actually left with after remitting.
  //
  // Server-side on purpose — fees.py warns that several places re-deriving this
  // arithmetic is how they end up disagreeing in the third decimal.
  getMembershipEarningsPreview: async (price, joiningFee = 0, gstRate = 0) => {
    const response = await api.get('/api/memberships/earnings-preview', {
      params: { price, joining_fee: joiningFee, gst_rate: gstRate },
    });
    return response.data;
  },

  // GST starting point for a category, with the reasoning attached. A
  // suggestion, never a determination.
  getMembershipTaxSuggestion: async (category) => {
    const response = await api.get('/api/memberships/tax-suggestion', { params: { category } });
    return response.data;
  },


  // ── Desk staff ───────────────────────────────────────────────────────────
  //
  // A desk device holds its own token so nobody has to hand out the owner's
  // login to scan a pass. The token lives in localStorage on the counter
  // tablet, NOT sessionStorage — a tablet that reboots must come back linked,
  // or somebody has to find the owner before the doors open.

  STAFF_TOKEN_KEY: 'mintlink_staff_token',

  getStaffToken: () => {
    try { return window.localStorage.getItem('mintlink_staff_token') || ''; }
    catch { return ''; }
  },

  setStaffToken: (token) => {
    try {
      if (token) window.localStorage.setItem('mintlink_staff_token', token);
      else window.localStorage.removeItem('mintlink_staff_token');
    } catch { /* private mode — the desk falls back to the creator login */ }
  },

  listMembershipStaff: async () => {
    const response = await api.get('/api/memberships/staff');
    return response.data;
  },

  // Returns the one-time join code. It is shown once and never again — a code
  // that stays on a settings page enrols a device months after the person it
  // was meant for has left.
  addMembershipStaff: async (name, note = '') => {
    const response = await api.post('/api/memberships/staff', { name, note });
    return response.data;
  },

  revokeMembershipStaff: async (staffId) => {
    const response = await api.delete(`/api/memberships/staff/${staffId}`);
    return response.data;
  },

  // Reissuing UNLINKS the current device — for a replaced tablet, or a lost one.
  reissueStaffCode: async (staffId) => {
    const response = await api.post(`/api/memberships/staff/${staffId}/new-code`);
    return response.data;
  },

  // Unauthenticated by necessity: the person doing this has no account.
  linkStaffDevice: async (code) => {
    const response = await api.post('/api/memberships/staff/link', { code });
    return response.data;
  },

  getStaffMe: async () => {
    const response = await api.get('/api/memberships/staff/me', {
      headers: { 'X-Staff-Token': apiService.getStaffToken() },
    });
    return response.data;
  },

  // ── Member photo, so the desk can see a face ─────────────────────────────
  uploadMemberPhoto: async (token, file) => {
    const form = new FormData();
    form.append('file', file);
    const response = await api.post(`/api/memberships/card/${token}/photo`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  removeMemberPhoto: async (token) => {
    const response = await api.delete(`/api/memberships/card/${token}/photo`);
    return response.data;
  },

  // ── Tax invoice ──────────────────────────────────────────────────────────
  //
  // Issued BY THE CREATOR, not by MintLink — every figure is read from the
  // frozen sale row so a later price change cannot rewrite history.
  getMemberPayments: async (token) => {
    const response = await api.get(`/api/memberships/card/${token}/payments`);
    return response.data;
  },

  getMemberInvoice: async (token, saleId) => {
    const response = await api.get(`/api/memberships/card/${token}/invoice/${saleId}`);
    return response.data;
  },

  // ── Admin ────────────────────────────────────────────────────────────────
  getAdminMembershipOverview: async (windowDays = 30) => {
    const response = await api.get('/api/admin/memberships/overview', {
      params: { window_days: windowDays },
    });
    return response.data;
  },

  getAdminMemberships: async (params = {}) => {
    const response = await api.get('/api/admin/memberships', { params });
    return response.data;
  },

  getAdminMembershipDetail: async (membershipId) => {
    const response = await api.get(`/api/admin/memberships/${membershipId}`);
    return response.data;
  },

  // Add-only, and a reason is required — it replaces hand-editing the database.
  extendAdminMembership: async (membershipId, days, reason) => {
    const response = await api.post(`/api/admin/memberships/${membershipId}/extend`, {
      days, reason,
    });
    return response.data;
  },

  // Every membership this buyer holds, across all creators.
  getBuyerMemberships: async () => {
    const token = safeSessionStorage.getItem('buyer_token');
    const response = await api.get('/api/buyers/me/memberships', {
      headers: token ? { 'X-Buyer-Token': token } : {},
    });
    return response.data;
  },

  // ============================================
  // CHECKOUT BRANDING (Plus) APIs
  // ============================================

  // The creator's saved checkout branding → { branding: {...} }
  getMyCheckoutBranding: async () => {
    const response = await api.get('/api/creators/me/checkout-branding');
    return response.data;
  },

  // Save/publish checkout branding (full object; see utils/plus.js DEFAULT_BRANDING)
  updateCheckoutBranding: async (branding) => {
    const response = await api.put('/api/creators/me/checkout-branding', branding);
    return response.data;
  },

  // Physical shop branding — separate design from the digital checkout above.
  getMyPhysicalCheckoutBranding: async () => {
    const response = await api.get('/api/creators/me/physical-checkout-branding');
    return response.data;
  },

  updatePhysicalCheckoutBranding: async (branding) => {
    const response = await api.put('/api/creators/me/physical-checkout-branding', branding);
    return response.data;
  },

  // ============================================
  // REVIEW MY WORK — SERVICE CRUD (creator + public)
  // ============================================

  // Cover image / example work uploads reuse getPresignedUploadUrl above
  // (upload_type: 'service_cover' | 'service_example'). data.example_files is
  // an optional array of {file_url,file_name,file_size} shown on the public
  // service page's "Examples" section.
  createReviewService: async (data) => {
    const response = await api.post('/api/services', data);
    return response.data;
  },

  getMyReviewServices: async () => {
    const response = await api.get('/api/creators/me/services');
    return response.data.services || [];
  },

  getReviewService: async (serviceId) => {
    const response = await api.get(`/api/services/id/${serviceId}`);
    return response.data;
  },

  updateReviewService: async (serviceId, updates) => {
    const response = await api.patch(`/api/services/${serviceId}`, updates);
    return response.data;
  },

  deleteReviewService: async (serviceId) => {
    const response = await api.delete(`/api/services/${serviceId}`);
    return response.data;
  },

  // Public — returns the service plus creator_stats {rating, review_count,
  // avg_delivery_hours, on_time_percent, repeat_customer_percent} and an
  // optional penalty_flag ('warning'|'reduced_visibility'|'suspended').
  getReviewServiceBySlug: async (slug) => {
    const response = await api.get(`/api/services/${slug}`);
    return response.data;
  },

  // Slot-capacity pre-check — UX only, not a substitute for the server-side
  // hard block at order-creation time (two buyers can race the last slot).
  getReviewServiceAvailability: async (serviceId) => {
    const response = await api.get(`/api/services/${serviceId}/availability`);
    return response.data;
  },

  getServicesMarketplace: async (params = {}) => {
    const response = await api.get('/api/services/marketplace', { params });
    return response.data;
  },

  // ============================================
  // REVIEW MY WORK — ORDER LIFECYCLE
  // ============================================

  // Buyer registers pre-uploaded file references + notes; starts the
  // creator's deadline clock. Works either via a logged-in buyer session
  // (X-Buyer-Token) or an order-specific access token from the confirmation
  // link/email (no buyer login required) — see getOrderAccessToken.
  uploadReviewOrderSubmission: async (orderId, { files, buyer_notes }, orderToken) => {
    const token = safeSessionStorage.getItem('buyer_token');
    const response = await api.post(
      `/api/buyers/me/review-orders/${orderId}/submit`,
      { files, buyer_notes },
      {
        headers: token ? { 'X-Buyer-Token': token } : {},
        params: !token && orderToken ? { order_token: orderToken } : undefined,
      },
    );
    return response.data;
  },

  // Creator (Firebase auth via interceptor)
  getMyReviewOrders: async (params = {}) => {
    const response = await api.get('/api/creators/me/review-orders', { params });
    return response.data;
  },

  getMyReviewOrder: async (orderId) => {
    const response = await api.get(`/api/creators/me/review-orders/${orderId}`);
    return response.data;
  },

  startReviewOrder: async (orderId) => {
    const response = await api.post(`/api/creators/me/review-orders/${orderId}/start-review`);
    return response.data;
  },

  deliverReviewOrder: async (orderId, { files, message }) => {
    const response = await api.post(`/api/creators/me/review-orders/${orderId}/deliver`, { files, message });
    return response.data;
  },

  // Buyer (buyer_token auth)
  getBuyerReviewOrders: async () => {
    const token = safeSessionStorage.getItem('buyer_token');
    const response = await api.get('/api/buyers/me/review-orders', {
      headers: token ? { 'X-Buyer-Token': token } : {},
    });
    return response.data;
  },

  getBuyerReviewOrder: async (orderId, orderToken) => {
    const token = safeSessionStorage.getItem('buyer_token');
    const response = await api.get(`/api/buyers/me/review-orders/${orderId}`, {
      headers: token ? { 'X-Buyer-Token': token } : {},
      params: !token && orderToken ? { order_token: orderToken } : undefined,
    });
    return response.data;
  },

  completeReviewOrder: async (orderId, orderToken) => {
    const token = safeSessionStorage.getItem('buyer_token');
    const response = await api.post(
      `/api/buyers/me/review-orders/${orderId}/complete`,
      {},
      {
        headers: token ? { 'X-Buyer-Token': token } : {},
        params: !token && orderToken ? { order_token: orderToken } : undefined,
      },
    );
    return response.data;
  },

  // Parallel to submitReview (which is keyed by sale_id) rather than an
  // overload of it, since it's not yet known whether the backend models a
  // review order as a `sale` row — safe either way, backend can alias later.
  submitReviewOrderRating: async (orderId, { rating, comment }, orderToken) => {
    const token = safeSessionStorage.getItem('buyer_token');
    const response = await api.post(
      `/api/buyers/me/review-orders/${orderId}/rating`,
      { rating, comment },
      {
        headers: token ? { 'X-Buyer-Token': token } : {},
        params: !token && orderToken ? { order_token: orderToken } : undefined,
      },
    );
    return response.data;
  },

  // ============================================
  // REVIEW MY WORK — PER-ORDER CHAT
  // ============================================
  // Dual-auth: backend reads whichever of the Firebase bearer token (creator,
  // via the request interceptor), X-Buyer-Token (logged-in buyer), or
  // order_token query param (guest buyer via their order link) is present.

  getReviewOrderMessages: async (orderId, orderToken) => {
    const buyerToken = safeSessionStorage.getItem('buyer_token');
    const response = await api.get(`/api/review-orders/${orderId}/messages`, {
      headers: buyerToken ? { 'X-Buyer-Token': buyerToken } : {},
      params: !buyerToken && orderToken ? { order_token: orderToken } : undefined,
    });
    return response.data;
  },

  postReviewOrderMessage: async (orderId, { message, attachment_url }, orderToken) => {
    const buyerToken = safeSessionStorage.getItem('buyer_token');
    const response = await api.post(
      `/api/review-orders/${orderId}/messages`,
      { message, attachment_url },
      {
        headers: buyerToken ? { 'X-Buyer-Token': buyerToken } : {},
        params: !buyerToken && orderToken ? { order_token: orderToken } : undefined,
      },
    );
    return response.data;
  },

  // ============================================
  // SERVICES (freelance marketplace) — LISTING CRUD
  // ============================================
  //
  // Kept on its own `/api/gigs` namespace rather than folded into the generic
  // `/api/services` above: that path is Review My Work's, its response shape is
  // a single flat price + page count, and a listing here is a three-package
  // table with extras and a buyer-brief form. One endpoint returning either
  // shape depending on a service_type field is how both end up half-supported.
  //
  // Cover/gallery uploads reuse getPresignedUploadUrl with
  // upload_type: 'gig_cover' | 'gig_gallery'.

  createGig: async (data) => {
    const response = await api.post('/api/gigs', data);
    return response.data;
  },

  getMyGigs: async () => {
    const response = await api.get('/api/creators/me/gigs');
    return response.data.gigs || [];
  },

  getGig: async (gigId) => {
    const response = await api.get(`/api/gigs/id/${gigId}`);
    return response.data;
  },

  updateGig: async (gigId, updates) => {
    const response = await api.patch(`/api/gigs/${gigId}`, updates);
    return response.data;
  },

  deleteGig: async (gigId) => {
    const response = await api.delete(`/api/gigs/${gigId}`);
    return response.data;
  },

  // Pause/resume without deleting — a seller going on holiday should not have
  // to destroy a listing (and its reviews and ranking) to stop taking orders.
  setGigPaused: async (gigId, paused) => {
    const response = await api.patch(`/api/gigs/${gigId}`, { is_paused: !!paused });
    return response.data;
  },

  // Public — the listing plus seller_stats {rating, review_count,
  // orders_completed, avg_response_hours, on_time_percent, repeat_buyer_percent}.
  getGigBySlug: async (slug) => {
    const response = await api.get(`/api/gigs/${slug}`);
    return response.data;
  },

  getGigsMarketplace: async (params = {}) => {
    const response = await api.get('/api/gigs/marketplace', { params });
    return response.data;
  },

  // Seller's GST declaration. Stored on the creator, not per-listing, so a
  // seller who registers doesn't have to remember to update six listings —
  // each listing only carries the on/off toggle and the rate.
  getGigTaxProfile: async () => {
    const response = await api.get('/api/creators/me/gig-tax-profile');
    return response.data;
  },

  updateGigTaxProfile: async (body) => {
    const response = await api.put('/api/creators/me/gig-tax-profile', body);
    return response.data;
  },

  // ============================================
  // SERVICES — ORDER LIFECYCLE
  // ============================================
  //
  // Escrow works exactly as it does for Review My Work: the buyer pays up
  // front, MintLink holds the money, and it is released to the seller only on
  // completion (buyer confirms, or auto-completion 3 days after delivery).
  //
  // Auth is the same three-way pattern used throughout: Firebase bearer for
  // the seller (added by the request interceptor), X-Buyer-Token for a
  // logged-in buyer, or an order-specific `order_token` for a guest buyer
  // arriving from their confirmation email.

  // Seller (Firebase auth via interceptor)
  getMyGigOrders: async (params = {}) => {
    const response = await api.get('/api/creators/me/gig-orders', { params });
    return response.data;
  },

  getMyGigOrder: async (orderId) => {
    const response = await api.get(`/api/creators/me/gig-orders/${orderId}`);
    return response.data;
  },

  startGigOrder: async (orderId) => {
    const response = await api.post(`/api/creators/me/gig-orders/${orderId}/start`);
    return response.data;
  },

  // Also used to re-deliver after a revision request — same endpoint, the
  // backend increments revisions_used when the order was in revision_requested.
  deliverGigOrder: async (orderId, { files, message }) => {
    const response = await api.post(`/api/creators/me/gig-orders/${orderId}/deliver`, { files, message });
    return response.data;
  },

  // Seller-initiated cancellation. Refunds the buyer in full (less the
  // non-refundable buyer service fee) and counts against the seller's
  // completion rate — never a silent way out of a job.
  cancelGigOrder: async (orderId, { reason }) => {
    const response = await api.post(`/api/creators/me/gig-orders/${orderId}/cancel`, { reason });
    return response.data;
  },

  // Seller asks for a deadline extension; the buyer must accept it.
  requestGigDeadlineExtension: async (orderId, { extra_days, reason }) => {
    const response = await api.post(
      `/api/creators/me/gig-orders/${orderId}/request-extension`,
      { extra_days, reason },
    );
    return response.data;
  },

  // Buyer
  getBuyerGigOrders: async () => {
    const token = safeSessionStorage.getItem('buyer_token');
    const response = await api.get('/api/buyers/me/gig-orders', {
      headers: token ? { 'X-Buyer-Token': token } : {},
    });
    return response.data;
  },

  getBuyerGigOrder: async (orderId, orderToken) => {
    const token = safeSessionStorage.getItem('buyer_token');
    const response = await api.get(`/api/buyers/me/gig-orders/${orderId}`, {
      headers: token ? { 'X-Buyer-Token': token } : {},
      params: !token && orderToken ? { order_token: orderToken } : undefined,
    });
    return response.data;
  },

  // The brief. Submitting it — not the payment — starts the delivery clock,
  // so the seller is never held to a deadline for work they can't begin.
  submitGigRequirements: async (orderId, { answers, files }, orderToken) => {
    const token = safeSessionStorage.getItem('buyer_token');
    const response = await api.post(
      `/api/buyers/me/gig-orders/${orderId}/requirements`,
      { answers, files },
      {
        headers: token ? { 'X-Buyer-Token': token } : {},
        params: !token && orderToken ? { order_token: orderToken } : undefined,
      },
    );
    return response.data;
  },

  requestGigRevision: async (orderId, { message }, orderToken) => {
    const token = safeSessionStorage.getItem('buyer_token');
    const response = await api.post(
      `/api/buyers/me/gig-orders/${orderId}/request-revision`,
      { message },
      {
        headers: token ? { 'X-Buyer-Token': token } : {},
        params: !token && orderToken ? { order_token: orderToken } : undefined,
      },
    );
    return response.data;
  },

  // Buyer's own cancel — deliberately narrower than the seller's: the backend
  // only allows this once the order's deadline has actually passed (still
  // on-time work in progress can't be cancelled out from under a seller).
  cancelGigOrderAsBuyer: async (orderId, { reason } = {}, orderToken) => {
    const token = safeSessionStorage.getItem('buyer_token');
    const response = await api.post(
      `/api/buyers/me/gig-orders/${orderId}/cancel`,
      { reason },
      {
        headers: token ? { 'X-Buyer-Token': token } : {},
        params: !token && orderToken ? { order_token: orderToken } : undefined,
      },
    );
    return response.data;
  },

  // Accepting the delivery — this is what releases escrow to the seller.
  completeGigOrder: async (orderId, orderToken) => {
    const token = safeSessionStorage.getItem('buyer_token');
    const response = await api.post(
      `/api/buyers/me/gig-orders/${orderId}/complete`,
      {},
      {
        headers: token ? { 'X-Buyer-Token': token } : {},
        params: !token && orderToken ? { order_token: orderToken } : undefined,
      },
    );
    return response.data;
  },

  submitGigOrderRating: async (orderId, { rating, comment }, orderToken) => {
    const token = safeSessionStorage.getItem('buyer_token');
    const response = await api.post(
      `/api/buyers/me/gig-orders/${orderId}/rating`,
      { rating, comment },
      {
        headers: token ? { 'X-Buyer-Token': token } : {},
        params: !token && orderToken ? { order_token: orderToken } : undefined,
      },
    );
    return response.data;
  },

  respondToGigExtension: async (orderId, { accept }, orderToken) => {
    const token = safeSessionStorage.getItem('buyer_token');
    const response = await api.post(
      `/api/buyers/me/gig-orders/${orderId}/extension-response`,
      { accept: !!accept },
      {
        headers: token ? { 'X-Buyer-Token': token } : {},
        params: !token && orderToken ? { order_token: orderToken } : undefined,
      },
    );
    return response.data;
  },

  // Public reviews left on a listing, for the buyer-facing page.
  getGigReviews: async (slug, { limit = 20, lastKey } = {}) => {
    const response = await api.get(`/api/gigs/${slug}/reviews`, {
      params: { limit, ...(lastKey ? { last_key: lastKey } : {}) },
    });
    return response.data;
  },

  // ============================================
  // SERVICES — PER-ORDER CHAT
  // ============================================
  // Same dual-auth contract as the Review My Work chat above.

  getGigOrderMessages: async (orderId, orderToken) => {
    const buyerToken = safeSessionStorage.getItem('buyer_token');
    const response = await api.get(`/api/gig-orders/${orderId}/messages`, {
      headers: buyerToken ? { 'X-Buyer-Token': buyerToken } : {},
      params: !buyerToken && orderToken ? { order_token: orderToken } : undefined,
    });
    return response.data;
  },

  postGigOrderMessage: async (orderId, { message, attachment_url }, orderToken) => {
    const buyerToken = safeSessionStorage.getItem('buyer_token');
    const response = await api.post(
      `/api/gig-orders/${orderId}/messages`,
      { message, attachment_url },
      {
        headers: buyerToken ? { 'X-Buyer-Token': buyerToken } : {},
        params: !buyerToken && orderToken ? { order_token: orderToken } : undefined,
      },
    );
    return response.data;
  },

  // ==========================================================================
  // COMMUNITY
  //
  // Creators-only, and pseudonymous: nothing these endpoints return carries a
  // creator_id. A member is identified by their generated handle and nothing
  // else, so there is no id here to pass around or accidentally log.
  //
  // getCommunityMeta() is the one to call first — it answers "has this person
  // joined yet", which decides between the feed and the join flow.
  // ==========================================================================
  getCommunityMeta: async () => {
    const response = await api.get('/api/community/meta');
    return response.data;
  },

  joinCommunity: async (payload) => {
    const response = await api.post('/api/community/join', payload);
    return response.data;
  },

  getCommunityMe: async () => {
    const response = await api.get('/api/community/me');
    return response.data;
  },

  rerollCommunityIdentity: async (payload) => {
    const response = await api.post('/api/community/me/reroll', payload);
    return response.data;
  },

  acceptCommunityRules: async () => {
    const response = await api.post('/api/community/me/accept-rules');
    return response.data;
  },

  getCommunityPosts: async (params = {}) => {
    const response = await api.get('/api/community/posts', { params });
    return response.data;
  },

  createCommunityPost: async (payload) => {
    const response = await api.post('/api/community/posts', payload);
    return response.data;
  },

  getCommunityPost: async (postId) => {
    const response = await api.get(`/api/community/posts/${postId}`);
    return response.data;
  },

  updateCommunityPost: async (postId, payload) => {
    const response = await api.patch(`/api/community/posts/${postId}`, payload);
    return response.data;
  },

  deleteCommunityPost: async (postId) => {
    const response = await api.delete(`/api/community/posts/${postId}`);
    return response.data;
  },

  addCommunityComment: async (postId, payload) => {
    const response = await api.post(`/api/community/posts/${postId}/comments`, payload);
    return response.data;
  },

  deleteCommunityComment: async (postId, commentId) => {
    const response = await api.delete(`/api/community/posts/${postId}/comments/${commentId}`);
    return response.data;
  },

  markCommunityCommentHelpful: async (postId, commentId) => {
    const response = await api.post(`/api/community/posts/${postId}/helpful/${commentId}`);
    return response.data;
  },

  voteCommunityPost: async (postId, value) => {
    const response = await api.post(`/api/community/vote/${postId}`, { value });
    return response.data;
  },

  // A comment can only be addressed through the post whose partition it lives
  // in, so this needs both ids — unlike the post vote above.
  voteCommunityComment: async (postId, commentId, value) => {
    const response = await api.post(
      `/api/community/posts/${postId}/comments/${commentId}/vote`, { value },
    );
    return response.data;
  },

  voteCommunityPoll: async (postId, optionId) => {
    const response = await api.post(`/api/community/posts/${postId}/poll-vote`, { option_id: optionId });
    return response.data;
  },

  reportCommunityContent: async (targetId, { reason, note }, postId) => {
    const response = await api.post(
      `/api/community/report/${targetId}`,
      { reason, note },
      { params: postId ? { post_id: postId } : undefined },
    );
    return response.data;
  },

  getCommunityLeaderboard: async () => {
    const response = await api.get('/api/community/leaderboard');
    return response.data;
  },

  // Returns the whole catalogue including locked entries — the locked ones are
  // the point, they're what tells someone what to do next.
  getCommunityAchievements: async () => {
    const response = await api.get('/api/community/me/achievements');
    return response.data;
  },
};

export default api;